import dagre from 'dagre';
import ELK from 'elkjs/lib/elk.bundled.js';
import type { UnifiedDiagramNode, UnifiedDiagramEdge, DiagramMetadata } from '@veodiagram/shared';

/**
 * Layout direction options.
 */
export type LayoutDirection = 'TB' | 'LR' | 'BT' | 'RL';

/**
 * Layout algorithm options.
 */
export type LayoutAlgorithm = 'dagre' | 'elk' | 'force' | 'radial' | 'tree';

/**
 * Layout options configuration.
 */
export interface LayoutOptions {
  direction: LayoutDirection;
  algorithm: LayoutAlgorithm;
  nodeWidth: number;
  nodeHeight: number;
  nodeSpacing: number;
  rankSpacing: number;
}

const defaultLayoutOptions: LayoutOptions = {
  direction: 'TB',
  algorithm: 'dagre',
  nodeWidth: 180,
  nodeHeight: 60,
  nodeSpacing: 80,   // Increased from 50 for better label spacing
  rankSpacing: 120,  // Increased from 80 for better label spacing
};

/**
 * Issue #287, #388: Calculate dynamic node dimensions based on label length.
 * This prevents nodes with long labels from overlapping.
 * Updated to match actual CSS constraints (min-w-[140px] max-w-[200px]).
 */
export function getNodeDimensions(
  node: UnifiedDiagramNode,
  defaults: { width: number; height: number; minWidth?: number; maxWidth?: number } = {
    width: defaultLayoutOptions.nodeWidth,
    height: defaultLayoutOptions.nodeHeight,
    minWidth: 140,  // Match CSS min-w-[140px]
    maxWidth: 200   // Match CSS max-w-[200px]
  }
): { width: number; height: number } {
  // If node has explicit dimensions, use them
  if (node.width && node.height) {
    return { width: node.width, height: node.height };
  }

  // Estimate width based on label length (~7px per character + padding for icon/badges)
  const label = node.label || node.id || '';
  const estimatedWidth = label.length * 7 + 48;

  // Clamp to CSS min/max constraints
  const minWidth = defaults.minWidth ?? 140;
  const maxWidth = defaults.maxWidth ?? 200;
  const clampedWidth = Math.max(minWidth, Math.min(maxWidth, estimatedWidth));

  // Check for subtitle or additional data that might need more height
  const hasSubtitle = node.data?.subtitle || node.data?.description;
  const hasModule = node.data?.module;
  const hasStats = node.data?.calledCount || node.data?.callCount;
  const hasBadges = node.data?.isAsync || node.data?.type === 'constructor';

  // Calculate height based on content
  let estimatedHeight = defaults.height; // Base 60px
  if (hasSubtitle || hasModule) estimatedHeight += 14; // Module/subtitle line
  if (hasStats) estimatedHeight += 14; // Stats line
  if (hasBadges) estimatedHeight += 8; // Extra padding for badges

  return { width: clampedWidth, height: Math.max(defaults.height, estimatedHeight) };
}

/**
 * Issue #287, #388: Post-layout collision resolution.
 * Iteratively pushes apart overlapping nodes to prevent overlap.
 * Applied after dagre/ELK layouts which may still produce overlaps.
 * Enhanced with adaptive padding and convergence detection.
 */
export function resolveCollisions(
  nodes: UnifiedDiagramNode[],
  options: {
    padding?: number;
    maxIterations?: number;
    adaptivePadding?: boolean;
  } | number = {}
): UnifiedDiagramNode[] {
  if (nodes.length < 2) return nodes;

  // Support legacy signature (padding as number)
  const opts = typeof options === 'number'
    ? { padding: options }
    : options;

  const {
    padding = 20,
    maxIterations = Math.min(100, Math.max(50, nodes.length * 2)), // Scale with node count
    adaptivePadding = true
  } = opts;

  // Calculate adaptive padding based on graph density
  const effectivePadding = adaptivePadding
    ? Math.max(padding, 15 + Math.floor(nodes.length / 20) * 5)
    : padding;

  // Create mutable copies of positions
  const result = nodes.map(n => ({
    ...n,
    position: { ...n.position },
    width: n.width || defaultLayoutOptions.nodeWidth,
    height: n.height || defaultLayoutOptions.nodeHeight,
  }));

  let totalMovement = Infinity;
  const minMovementThreshold = 0.5; // Convergence threshold

  for (let iter = 0; iter < maxIterations && totalMovement > minMovementThreshold; iter++) {
    totalMovement = 0;

    for (let i = 0; i < result.length; i++) {
      const nodeA = result[i];
      for (let j = i + 1; j < result.length; j++) {
        const nodeB = result[j];

        // Calculate bounding box overlap
        const halfWidthA = nodeA.width / 2;
        const halfWidthB = nodeB.width / 2;
        const halfHeightA = nodeA.height / 2;
        const halfHeightB = nodeB.height / 2;

        // Center positions
        const centerAX = nodeA.position.x + halfWidthA;
        const centerAY = nodeA.position.y + halfHeightA;
        const centerBX = nodeB.position.x + halfWidthB;
        const centerBY = nodeB.position.y + halfHeightB;

        const dx = centerBX - centerAX;
        const dy = centerBY - centerAY;

        const minDistX = halfWidthA + halfWidthB + effectivePadding;
        const minDistY = halfHeightA + halfHeightB + effectivePadding;

        const overlapX = minDistX - Math.abs(dx);
        const overlapY = minDistY - Math.abs(dy);

        // Check if there's actual overlap (both dimensions must overlap)
        if (overlapX > 0 && overlapY > 0) {
          // Push apart along the axis with smaller overlap (more efficient)
          if (overlapX < overlapY) {
            const pushX = (overlapX / 2) * Math.sign(dx || 1);
            nodeA.position.x -= pushX;
            nodeB.position.x += pushX;
            totalMovement += Math.abs(pushX) * 2;
          } else {
            const pushY = (overlapY / 2) * Math.sign(dy || 1);
            nodeA.position.y -= pushY;
            nodeB.position.y += pushY;
            totalMovement += Math.abs(pushY) * 2;
          }
        }
      }
    }
  }

  return result;
}

/**
 * Calculate dynamic spacing based on edge density and labels.
 * This helps prevent label overlap in dense diagrams.
 */
export function calculateDynamicSpacing(
  nodes: UnifiedDiagramNode[],
  edges: UnifiedDiagramEdge[],
  baseOptions: Partial<LayoutOptions> = {}
): { nodeSpacing: number; rankSpacing: number } {
  const baseNodeSpacing = baseOptions.nodeSpacing ?? defaultLayoutOptions.nodeSpacing;
  const baseRankSpacing = baseOptions.rankSpacing ?? defaultLayoutOptions.rankSpacing;

  if (nodes.length === 0) {
    return { nodeSpacing: baseNodeSpacing, rankSpacing: baseRankSpacing };
  }

  // Calculate average degree (connectivity)
  const avgDegree = (edges.length * 2) / nodes.length;

  // Check if edges have labels (need extra spacing)
  const hasLabels = edges.some(e => e.label && e.label.trim().length > 0);
  const labelSpaceNeeded = hasLabels ? 30 : 0;

  // Calculate density factor (more edges = more spacing needed)
  const densityFactor = Math.min(avgDegree * 8, 40);

  // Final spacing calculation
  const nodeSpacing = Math.max(baseNodeSpacing, 80 + densityFactor + labelSpaceNeeded);
  const rankSpacing = Math.max(baseRankSpacing, 120 + densityFactor + labelSpaceNeeded);

  return { nodeSpacing, rankSpacing };
}

/**
 * Apply dagre layout to nodes and edges.
 * Issue #231: Reset positions to ensure visible layout changes.
 * Issue #287: Dynamic node dimensions and collision resolution.
 * Enhanced: Dynamic spacing based on edge density and labels.
 */
export function applyDagreLayout(
  nodes: UnifiedDiagramNode[],
  edges: UnifiedDiagramEdge[],
  options: Partial<LayoutOptions> = {}
): UnifiedDiagramNode[] {
  const opts = { ...defaultLayoutOptions, ...options };

  // Calculate dynamic spacing based on diagram density
  const { nodeSpacing, rankSpacing } = calculateDynamicSpacing(nodes, edges, opts);

  // Create a new dagre graph
  const g = new dagre.graphlib.Graph();
  g.setGraph({
    rankdir: opts.direction,
    nodesep: nodeSpacing,
    ranksep: rankSpacing,
    edgesep: 35,           // Issue #287: Increased edge separation for better spacing
    marginx: 50,           // Increased margin
    marginy: 50,           // Increased margin
    ranker: 'network-simplex', // Better edge routing
  });
  g.setDefaultEdgeLabel(() => ({}));

  // Issue #287: Add nodes with dynamic dimensions based on label length
  nodes.forEach((node) => {
    const dims = getNodeDimensions(node, { width: opts.nodeWidth, height: opts.nodeHeight });
    g.setNode(node.id, {
      width: dims.width,
      height: dims.height,
    });
  });

  // Add edges to the graph
  edges.forEach((edge) => {
    g.setEdge(edge.source, edge.target);
  });

  // Run the layout algorithm
  dagre.layout(g);

  // Update node positions with dynamic dimensions
  let layoutedNodes = nodes.map((node) => {
    const dagreNode = g.node(node.id);
    const dims = getNodeDimensions(node, { width: opts.nodeWidth, height: opts.nodeHeight });
    if (dagreNode) {
      return {
        ...node,
        width: dims.width,
        height: dims.height,
        position: {
          x: dagreNode.x - dims.width / 2,
          y: dagreNode.y - dims.height / 2,
        },
      };
    }
    return node;
  });

  // Issue #287: Apply collision resolution as a final pass
  layoutedNodes = resolveCollisions(layoutedNodes, 15);

  return layoutedNodes;
}

/**
 * ELK instance for ELK layout algorithm.
 */
const elk = new ELK();

/**
 * Apply ELK layout to nodes and edges.
 * Better for complex diagrams like state machines.
 * Issue #287: Dynamic node dimensions and collision resolution.
 * Enhanced: Dynamic spacing and edge routing optimization.
 */
export async function applyElkLayout(
  nodes: UnifiedDiagramNode[],
  edges: UnifiedDiagramEdge[],
  options: Partial<LayoutOptions> = {}
): Promise<UnifiedDiagramNode[]> {
  const opts = { ...defaultLayoutOptions, ...options };

  // Calculate dynamic spacing based on diagram density
  const { nodeSpacing, rankSpacing } = calculateDynamicSpacing(nodes, edges, opts);

  // Issue #287: Apply spacing multiplier for better separation
  const spacingMultiplier = 1.3;
  const adjustedNodeSpacing = Math.round(nodeSpacing * spacingMultiplier);
  const adjustedRankSpacing = Math.round(rankSpacing * spacingMultiplier);

  // Convert direction to ELK format
  const directionMap = {
    TB: 'DOWN',
    LR: 'RIGHT',
    BT: 'UP',
    RL: 'LEFT',
  } as const;

  // Issue #287: Pre-calculate dynamic dimensions for all nodes
  const nodeDimensions = new Map<string, { width: number; height: number }>();
  nodes.forEach((node) => {
    nodeDimensions.set(node.id, getNodeDimensions(node, { width: opts.nodeWidth, height: opts.nodeHeight }));
  });

  const graph = {
    id: 'root',
    layoutOptions: {
      'elk.algorithm': 'layered',
      'elk.direction': directionMap[opts.direction],
      'elk.spacing.nodeNode': String(adjustedNodeSpacing),
      'elk.layered.spacing.nodeNodeBetweenLayers': String(adjustedRankSpacing),
      // Issue #287: Increased edge spacing for better separation
      'elk.spacing.edgeNode': '35',
      'elk.spacing.edgeEdge': '20',
      'elk.layered.spacing.edgeNodeBetweenLayers': '35',
      // Crossing minimization
      'elk.layered.crossingMinimization.strategy': 'LAYER_SWEEP',
      'elk.layered.nodePlacement.strategy': 'NETWORK_SIMPLEX',
      // Better label handling
      'elk.edgeLabels.inline': 'true',
      // Issue #287: Add component spacing
      'elk.spacing.componentComponent': String(adjustedNodeSpacing),
    },
    children: nodes.map((node) => {
      const dims = nodeDimensions.get(node.id)!;
      return {
        id: node.id,
        width: dims.width,
        height: dims.height,
      };
    }),
    edges: edges.map((edge) => ({
      id: edge.id,
      sources: [edge.source],
      targets: [edge.target],
    })),
  };

  // Type assertion needed due to ELK's strict typings for layout options
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const layoutedGraph = await elk.layout(graph as any);

  // Update node positions with dynamic dimensions
  let layoutedNodes = nodes.map((node) => {
    const elkNode = layoutedGraph.children?.find((n) => n.id === node.id);
    const dims = nodeDimensions.get(node.id)!;
    if (elkNode && elkNode.x !== undefined && elkNode.y !== undefined) {
      return {
        ...node,
        width: dims.width,
        height: dims.height,
        position: {
          x: elkNode.x,
          y: elkNode.y,
        },
      };
    }
    return node;
  });

  // Issue #287: Apply collision resolution as a final pass
  layoutedNodes = resolveCollisions(layoutedNodes, 15);

  return layoutedNodes;
}

/**
 * Apply simple force-directed layout.
 * Best for mesh/network diagrams without clear hierarchy.
 * Issue #231: Reset positions to ensure visible layout changes.
 * Enhanced: Stronger repulsion for better node separation.
 */
export function applyForceLayout(
  nodes: UnifiedDiagramNode[],
  edges: UnifiedDiagramEdge[],
  options: Partial<LayoutOptions & { resetPositions?: boolean }> = {}
): UnifiedDiagramNode[] {
  // Calculate dynamic spacing
  const { nodeSpacing, rankSpacing } = calculateDynamicSpacing(nodes, edges, options);

  // Simple force-directed simulation
  // Issue #231: Increased iterations for better convergence on large diagrams
  const iterations = Math.min(250, Math.max(120, nodes.length));
  // Increase repulsion for better separation (accounts for labels)
  const repulsion = 15000 + (nodeSpacing * 50);
  const attraction = 0.008;
  const damping = 0.92;
  const resetPositions = options.resetPositions ?? true; // Issue #231: Default to reset
  // Minimum distance between nodes
  const minDistance = Math.max(nodeSpacing, rankSpacing);

  // Initialize positions and velocities
  const positions = new Map<string, { x: number; y: number; vx: number; vy: number }>();

  nodes.forEach((node, index) => {
    // Issue #231: Always arrange in a circle initially to ensure layout changes are visible
    const angle = (2 * Math.PI * index) / nodes.length;
    const radius = Math.max(300, nodes.length * 40); // Increased radius
    const circleX = 500 + radius * Math.cos(angle);
    const circleY = 400 + radius * Math.sin(angle);
    positions.set(node.id, {
      x: resetPositions ? circleX : (node.position.x || circleX),
      y: resetPositions ? circleY : (node.position.y || circleY),
      vx: 0,
      vy: 0,
    });
  });

  // Run simulation
  for (let i = 0; i < iterations; i++) {
    // Apply repulsion between all nodes
    for (const nodeA of nodes) {
      const posA = positions.get(nodeA.id)!;
      for (const nodeB of nodes) {
        if (nodeA.id === nodeB.id) continue;
        const posB = positions.get(nodeB.id)!;

        const dx = posA.x - posB.x;
        const dy = posA.y - posB.y;
        const distance = Math.max(1, Math.sqrt(dx * dx + dy * dy));

        const force = repulsion / (distance * distance);
        const fx = (dx / distance) * force;
        const fy = (dy / distance) * force;

        posA.vx += fx;
        posA.vy += fy;
      }
    }

    // Apply attraction along edges
    for (const edge of edges) {
      const posSource = positions.get(edge.source);
      const posTarget = positions.get(edge.target);
      if (!posSource || !posTarget) continue;

      const dx = posTarget.x - posSource.x;
      const dy = posTarget.y - posSource.y;
      const distance = Math.sqrt(dx * dx + dy * dy);

      const force = distance * attraction;
      const fx = (dx / distance) * force;
      const fy = (dy / distance) * force;

      posSource.vx += fx;
      posSource.vy += fy;
      posTarget.vx -= fx;
      posTarget.vy -= fy;
    }

    // Apply velocities and damping
    for (const node of nodes) {
      const pos = positions.get(node.id)!;
      pos.x += pos.vx;
      pos.y += pos.vy;
      pos.vx *= damping;
      pos.vy *= damping;
    }

    // Enforce minimum distance between nodes (collision resolution)
    for (const nodeA of nodes) {
      const posA = positions.get(nodeA.id)!;
      for (const nodeB of nodes) {
        if (nodeA.id >= nodeB.id) continue; // Avoid double-checking
        const posB = positions.get(nodeB.id)!;

        const dx = posB.x - posA.x;
        const dy = posB.y - posA.y;
        const distance = Math.sqrt(dx * dx + dy * dy);

        if (distance < minDistance && distance > 0) {
          // Push nodes apart to meet minimum distance
          const overlap = (minDistance - distance) / 2;
          const nx = dx / distance;
          const ny = dy / distance;

          posA.x -= nx * overlap;
          posA.y -= ny * overlap;
          posB.x += nx * overlap;
          posB.y += ny * overlap;
        }
      }
    }
  }

  // Update node positions
  return nodes.map((node) => {
    const pos = positions.get(node.id);
    if (pos) {
      return {
        ...node,
        position: { x: pos.x, y: pos.y },
      };
    }
    return node;
  });
}

/**
 * Apply radial layout to nodes and edges.
 * Places the highest-degree node at center, then distributes other nodes
 * in concentric rings based on BFS hop distance.
 */
export function applyRadialLayout(
  nodes: UnifiedDiagramNode[],
  edges: UnifiedDiagramEdge[],
  options: Partial<LayoutOptions> = {}
): UnifiedDiagramNode[] {
  if (nodes.length === 0) return nodes;

  const opts = { ...defaultLayoutOptions, ...options };
  const { nodeSpacing } = calculateDynamicSpacing(nodes, edges, opts);
  const ringSpacing = Math.max(nodeSpacing * 2, 200);

  // Build adjacency list
  const adjacency = new Map<string, Set<string>>();
  nodes.forEach(n => adjacency.set(n.id, new Set()));
  edges.forEach(e => {
    adjacency.get(e.source)?.add(e.target);
    adjacency.get(e.target)?.add(e.source);
  });

  // Find center node: highest degree
  let centerId = nodes[0].id;
  let maxDegree = 0;
  adjacency.forEach((neighbors, id) => {
    if (neighbors.size > maxDegree) {
      maxDegree = neighbors.size;
      centerId = id;
    }
  });

  // BFS from center to compute hop distances
  const distances = new Map<string, number>();
  distances.set(centerId, 0);
  const queue = [centerId];
  let head = 0;
  while (head < queue.length) {
    const current = queue[head++];
    const dist = distances.get(current)!;
    for (const neighbor of adjacency.get(current) || []) {
      if (!distances.has(neighbor)) {
        distances.set(neighbor, dist + 1);
        queue.push(neighbor);
      }
    }
  }

  // Handle disconnected nodes: assign them to outermost ring + 1
  const maxDistance = Math.max(0, ...Array.from(distances.values()));
  const disconnectedRing = maxDistance + 1;
  nodes.forEach(n => {
    if (!distances.has(n.id)) {
      distances.set(n.id, disconnectedRing);
    }
  });

  // Group nodes by ring
  const rings = new Map<number, string[]>();
  distances.forEach((dist, id) => {
    if (!rings.has(dist)) rings.set(dist, []);
    rings.get(dist)!.push(id);
  });

  // Position nodes
  const positions = new Map<string, { x: number; y: number }>();
  const centerX = 0;
  const centerY = 0;

  rings.forEach((nodeIds, ring) => {
    if (ring === 0) {
      // Center node
      nodeIds.forEach(id => positions.set(id, { x: centerX, y: centerY }));
    } else {
      const radius = ring * ringSpacing;
      nodeIds.forEach((id, i) => {
        const angle = (2 * Math.PI * i) / nodeIds.length;
        positions.set(id, {
          x: centerX + radius * Math.cos(angle),
          y: centerY + radius * Math.sin(angle),
        });
      });
    }
  });

  // Apply positions with dynamic dimensions
  const positionedNodes: UnifiedDiagramNode[] = nodes.map(node => {
    const dims = getNodeDimensions(node, { width: opts.nodeWidth, height: opts.nodeHeight });
    const pos = positions.get(node.id) || { x: 0, y: 0 };
    return {
      ...node,
      width: dims.width,
      height: dims.height,
      position: { x: pos.x - dims.width / 2, y: pos.y - dims.height / 2 },
    };
  });

  return resolveCollisions(positionedNodes, 15);
}

/**
 * Apply tree layout with shared-node deduplication.
 * Nodes with inDegree > 1 are "shared" — only the primary parent edge
 * (from the highest out-degree source) is used for layout positioning.
 * Uses dagre with tight-tree ranker and wider spacing.
 */
export function applyTreeLayout(
  nodes: UnifiedDiagramNode[],
  edges: UnifiedDiagramEdge[],
  options: Partial<LayoutOptions> = {}
): UnifiedDiagramNode[] {
  if (nodes.length === 0) return nodes;

  const opts = { ...defaultLayoutOptions, ...options };
  const { nodeSpacing, rankSpacing } = calculateDynamicSpacing(nodes, edges, opts);

  // Compute in-degree and out-degree for each node
  const inDegree = new Map<string, number>();
  const outDegree = new Map<string, number>();
  const nodeSet = new Set(nodes.map(n => n.id));
  nodes.forEach(n => {
    inDegree.set(n.id, 0);
    outDegree.set(n.id, 0);
  });
  edges.forEach(e => {
    if (nodeSet.has(e.target)) inDegree.set(e.target, (inDegree.get(e.target) || 0) + 1);
    if (nodeSet.has(e.source)) outDegree.set(e.source, (outDegree.get(e.source) || 0) + 1);
  });

  // For shared nodes (inDegree > 1), keep only the primary parent edge for layout
  const sharedNodes = new Set<string>();
  inDegree.forEach((deg, id) => {
    if (deg > 1) sharedNodes.add(id);
  });

  const layoutEdges: UnifiedDiagramEdge[] = [];
  const edgesByTarget = new Map<string, UnifiedDiagramEdge[]>();
  edges.forEach(e => {
    if (!edgesByTarget.has(e.target)) edgesByTarget.set(e.target, []);
    edgesByTarget.get(e.target)!.push(e);
  });

  edgesByTarget.forEach((targetEdges, target) => {
    if (sharedNodes.has(target) && targetEdges.length > 1) {
      // Keep only the edge from the source with highest out-degree
      const primary = targetEdges.reduce((best, e) =>
        (outDegree.get(e.source) || 0) > (outDegree.get(best.source) || 0) ? e : best
      );
      layoutEdges.push(primary);
    } else {
      layoutEdges.push(...targetEdges);
    }
  });

  // Also include edges whose targets are not in edgesByTarget (shouldn't happen, but safe)
  edges.forEach(e => {
    if (!edgesByTarget.has(e.target)) layoutEdges.push(e);
  });

  // Run dagre with tight-tree ranker and wider spacing
  const g = new dagre.graphlib.Graph();
  g.setGraph({
    rankdir: opts.direction,
    nodesep: nodeSpacing + 20,
    ranksep: rankSpacing + 40,
    edgesep: 35,
    marginx: 50,
    marginy: 50,
    ranker: 'tight-tree',
  });
  g.setDefaultEdgeLabel(() => ({}));

  nodes.forEach(node => {
    const dims = getNodeDimensions(node, { width: opts.nodeWidth, height: opts.nodeHeight });
    g.setNode(node.id, { width: dims.width, height: dims.height });
  });

  layoutEdges.forEach(edge => {
    if (nodeSet.has(edge.source) && nodeSet.has(edge.target)) {
      g.setEdge(edge.source, edge.target);
    }
  });

  dagre.layout(g);

  let layoutedNodes = nodes.map(node => {
    const dagreNode = g.node(node.id);
    const dims = getNodeDimensions(node, { width: opts.nodeWidth, height: opts.nodeHeight });
    const isShared = sharedNodes.has(node.id);
    if (dagreNode) {
      return {
        ...node,
        width: dims.width,
        height: dims.height,
        position: {
          x: dagreNode.x - dims.width / 2,
          y: dagreNode.y - dims.height / 2,
        },
        data: {
          ...node.data,
          ...(isShared ? { isSharedNode: true, sharedByCount: inDegree.get(node.id) || 0 } : {}),
        },
      };
    }
    return node;
  });

  layoutedNodes = resolveCollisions(layoutedNodes, 15);
  return layoutedNodes;
}

// ============= Grouped Layout (Issue #565 Phase 1) =============

/**
 * Two-pass dagre layout for grouped import graphs.
 * Pass 1: lay out children within each group to compute group bounding boxes.
 * Pass 2: lay out the group nodes themselves.
 * Returns a flat node array — groups have absolute positions, children have
 * positions relative to their group's top-left origin (as required by React Flow).
 */
export function applyDagreGroupedLayout(
  nodes: UnifiedDiagramNode[],
  edges: UnifiedDiagramEdge[],
  options: Partial<LayoutOptions> = {}
): UnifiedDiagramNode[] {
  const opts = { ...defaultLayoutOptions, ...options };
  const GROUP_PADDING = 24;
  const GROUP_HEADER = 40;
  const MIN_GROUP_W = 160;
  const MIN_GROUP_H = 80;

  const groupParents = nodes.filter(n => n.type === 'importGroupNode');
  const children = nodes.filter(n => n.parentId !== undefined);
  const ungrouped = nodes.filter(n => n.type !== 'importGroupNode' && n.parentId === undefined);

  // Build per-group child lists
  const childrenByGroup = new Map<string, UnifiedDiagramNode[]>();
  for (const child of children) {
    const gid = child.parentId!;
    if (!childrenByGroup.has(gid)) childrenByGroup.set(gid, []);
    childrenByGroup.get(gid)!.push(child);
  }
  const nodeIdToGroupId = new Map<string, string>(children.map(c => [c.id, c.parentId!]));

  const layoutedChildren: UnifiedDiagramNode[] = [];
  const groupDimensions = new Map<string, { width: number; height: number }>();

  // --- Pass 1: lay out children within each group ---
  for (const [groupId, groupChildren] of childrenByGroup.entries()) {
    const internalEdges = edges.filter(
      e => nodeIdToGroupId.get(e.source) === groupId && nodeIdToGroupId.get(e.target) === groupId
    );
    const laidOut = applyDagreLayout(groupChildren, internalEdges, { ...opts, direction: 'TB' });

    let maxX = 0, maxY = 0;
    for (const n of laidOut) {
      maxX = Math.max(maxX, n.position.x + (n.width ?? opts.nodeWidth));
      maxY = Math.max(maxY, n.position.y + (n.height ?? opts.nodeHeight));
    }
    const groupW = Math.max(MIN_GROUP_W, maxX + GROUP_PADDING * 2);
    const groupH = Math.max(MIN_GROUP_H, maxY + GROUP_PADDING * 2 + GROUP_HEADER);
    groupDimensions.set(groupId, { width: groupW, height: groupH });

    // Children are positioned relative to group origin (React Flow parent-child)
    laidOut.forEach(n => {
      layoutedChildren.push({
        ...n,
        position: {
          x: n.position.x + GROUP_PADDING,
          y: n.position.y + GROUP_HEADER + GROUP_PADDING,
        },
      });
    });
  }

  // --- Pass 2: lay out group nodes ---
  const groupNodesWithDimensions = groupParents.map(g => ({
    ...g,
    width: groupDimensions.get(g.id)?.width ?? MIN_GROUP_W,
    height: groupDimensions.get(g.id)?.height ?? MIN_GROUP_H,
  }));

  // Cross-group aggregate edges for group layout
  const crossGroupEdges: UnifiedDiagramEdge[] = [];
  const seen = new Set<string>();
  for (const edge of edges) {
    const sg = nodeIdToGroupId.get(edge.source) ?? (edge.source.startsWith('group::') ? edge.source : undefined);
    const tg = nodeIdToGroupId.get(edge.target) ?? (edge.target.startsWith('group::') ? edge.target : undefined);
    if (sg && tg && sg !== tg) {
      const syntheticId = `ge::${sg}::${tg}`;
      if (!seen.has(syntheticId)) {
        seen.add(syntheticId);
        crossGroupEdges.push({ ...edge, id: syntheticId, source: sg, target: tg });
      }
    }
  }

  const laidOutGroups = applyDagreLayout(groupNodesWithDimensions, crossGroupEdges, opts);

  return [...laidOutGroups, ...layoutedChildren, ...ungrouped];
}

/**
 * Apply layout to diagram nodes based on metadata settings.
 */
export async function applyLayout(
  nodes: UnifiedDiagramNode[],
  edges: UnifiedDiagramEdge[],
  metadata: DiagramMetadata
): Promise<UnifiedDiagramNode[]> {
  const options: Partial<LayoutOptions> = {
    direction: metadata.layoutDirection,
    algorithm: metadata.layoutAlgorithm,
  };

  switch (metadata.layoutAlgorithm) {
    case 'elk':
      return applyElkLayout(nodes, edges, options);
    case 'force':
      return applyForceLayout(nodes, edges, options);
    case 'radial':
      return applyRadialLayout(nodes, edges, options);
    case 'tree':
      return applyTreeLayout(nodes, edges, options);
    case 'dagre':
    default:
      return applyDagreLayout(nodes, edges, options);
  }
}

/**
 * Auto-detect best layout algorithm based on diagram structure.
 * Issue #231: Improved detection to prefer dagre for dependency graphs.
 */
export function detectBestLayout(
  nodes: UnifiedDiagramNode[],
  edges: UnifiedDiagramEdge[]
): LayoutAlgorithm {
  // Calculate average degree
  const degrees = new Map<string, number>();
  const inDegrees = new Map<string, number>();
  const outDegrees = new Map<string, number>();

  nodes.forEach((n) => {
    degrees.set(n.id, 0);
    inDegrees.set(n.id, 0);
    outDegrees.set(n.id, 0);
  });

  edges.forEach((e) => {
    degrees.set(e.source, (degrees.get(e.source) || 0) + 1);
    degrees.set(e.target, (degrees.get(e.target) || 0) + 1);
    outDegrees.set(e.source, (outDegrees.get(e.source) || 0) + 1);
    inDegrees.set(e.target, (inDegrees.get(e.target) || 0) + 1);
  });

  const avgDegree = nodes.length > 0
    ? Array.from(degrees.values()).reduce((a, b) => a + b, 0) / nodes.length
    : 0;

  // Check for state machine patterns (self-loops, high connectivity)
  const hasSelfLoops = edges.some((e) => e.source === e.target);
  const isBidirectional = edges.some(
    (e) => edges.some((e2) => e2.source === e.target && e2.target === e.source)
  );

  // Issue #231: Check if this looks like a dependency graph (mostly one-directional flow)
  const bidirectionalCount = edges.filter(
    (e) => edges.some((e2) => e2.source === e.target && e2.target === e.source)
  ).length;
  const bidirectionalRatio = edges.length > 0 ? bidirectionalCount / edges.length : 0;

  // Check for hierarchical structure (some nodes have only outgoing, some only incoming)
  const sourceOnlyNodes = nodes.filter(n =>
    (outDegrees.get(n.id) || 0) > 0 && (inDegrees.get(n.id) || 0) === 0
  ).length;
  const sinkOnlyNodes = nodes.filter(n =>
    (inDegrees.get(n.id) || 0) > 0 && (outDegrees.get(n.id) || 0) === 0
  ).length;
  const hasHierarchy = sourceOnlyNodes > 0 || sinkOnlyNodes > 0;

  // State machines benefit from ELK
  if (hasSelfLoops || (isBidirectional && avgDegree > 2)) {
    return 'elk';
  }

  // Issue #231: Prefer dagre for dependency-style graphs
  // - Low bidirectional ratio (mostly one-way dependencies)
  // - Has clear hierarchy (some nodes are sources, some are sinks)
  // - Even with high degree, dagre handles DAGs well
  if (bidirectionalRatio < 0.3 || hasHierarchy) {
    return 'dagre';
  }

  // Highly connected mesh diagrams benefit from force layout
  if (avgDegree > 4 && bidirectionalRatio > 0.3) {
    return 'force';
  }

  // Default to dagre for hierarchical structures
  return 'dagre';
}

// ============= ELK Edge Routing (Issue #414) =============

/**
 * A point in 2D space for edge routing
 */
export interface EdgeBendPoint {
  x: number;
  y: number;
}

/**
 * Edge routing information from ELK
 */
export interface EdgeRoutingInfo {
  /** Edge ID */
  id: string;
  /** Bend points for routing around obstacles */
  bendPoints: EdgeBendPoint[];
  /** Start point of the edge */
  startPoint?: EdgeBendPoint;
  /** End point of the edge */
  endPoint?: EdgeBendPoint;
}

/**
 * Result of ELK layout with edge routing
 */
export interface ElkLayoutResult {
  /** Nodes with updated positions */
  nodes: UnifiedDiagramNode[];
  /** Edge routing information with bend points */
  edgeRouting: Map<string, EdgeRoutingInfo>;
}

/**
 * Apply ELK layout with edge routing information.
 * Issue #414: Returns both node positions and edge bend points for obstacle avoidance.
 *
 * @param nodes - Nodes to layout
 * @param edges - Edges to route
 * @param options - Layout options
 * @returns Layout result with node positions and edge routing
 */
export async function applyElkLayoutWithEdgeRouting(
  nodes: UnifiedDiagramNode[],
  edges: UnifiedDiagramEdge[],
  options: Partial<LayoutOptions> = {}
): Promise<ElkLayoutResult> {
  const opts = { ...defaultLayoutOptions, ...options };

  // Calculate dynamic spacing
  const { nodeSpacing, rankSpacing } = calculateDynamicSpacing(nodes, edges, opts);
  const spacingMultiplier = 1.3;
  const adjustedNodeSpacing = Math.round(nodeSpacing * spacingMultiplier);
  const adjustedRankSpacing = Math.round(rankSpacing * spacingMultiplier);

  // Convert direction to ELK format
  const directionMap = {
    TB: 'DOWN',
    LR: 'RIGHT',
    BT: 'UP',
    RL: 'LEFT',
  } as const;

  // Pre-calculate node dimensions
  const nodeDimensions = new Map<string, { width: number; height: number }>();
  nodes.forEach((node) => {
    nodeDimensions.set(node.id, getNodeDimensions(node, { width: opts.nodeWidth, height: opts.nodeHeight }));
  });

  const graph = {
    id: 'root',
    layoutOptions: {
      'elk.algorithm': 'layered',
      'elk.direction': directionMap[opts.direction],
      'elk.spacing.nodeNode': String(adjustedNodeSpacing),
      'elk.layered.spacing.nodeNodeBetweenLayers': String(adjustedRankSpacing),
      // Edge routing options for bend points
      'elk.spacing.edgeNode': '40',
      'elk.spacing.edgeEdge': '25',
      'elk.layered.spacing.edgeNodeBetweenLayers': '40',
      // Edge routing strategy
      'elk.edgeRouting': 'ORTHOGONAL',
      'elk.layered.edgeRouting.splines.mode': 'CONSERVATIVE',
      // Crossing minimization
      'elk.layered.crossingMinimization.strategy': 'LAYER_SWEEP',
      'elk.layered.nodePlacement.strategy': 'NETWORK_SIMPLEX',
      // Component spacing
      'elk.spacing.componentComponent': String(adjustedNodeSpacing),
    },
    children: nodes.map((node) => {
      const dims = nodeDimensions.get(node.id)!;
      return {
        id: node.id,
        width: dims.width,
        height: dims.height,
      };
    }),
    edges: edges.map((edge) => ({
      id: edge.id,
      sources: [edge.source],
      targets: [edge.target],
    })),
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const layoutedGraph = await elk.layout(graph as any);

  // Extract node positions
  let layoutedNodes = nodes.map((node) => {
    const elkNode = layoutedGraph.children?.find((n) => n.id === node.id);
    const dims = nodeDimensions.get(node.id)!;
    if (elkNode && elkNode.x !== undefined && elkNode.y !== undefined) {
      return {
        ...node,
        width: dims.width,
        height: dims.height,
        position: {
          x: elkNode.x,
          y: elkNode.y,
        },
      };
    }
    return node;
  });

  // Apply collision resolution
  layoutedNodes = resolveCollisions(layoutedNodes, 15);

  // Extract edge routing information
  const edgeRouting = new Map<string, EdgeRoutingInfo>();

  if (layoutedGraph.edges) {
    for (const elkEdge of layoutedGraph.edges) {
      const routingInfo: EdgeRoutingInfo = {
        id: elkEdge.id,
        bendPoints: [],
      };

      // Extract bend points from ELK edge sections
      if (elkEdge.sections && elkEdge.sections.length > 0) {
        for (const section of elkEdge.sections) {
          // Add start point
          if (section.startPoint) {
            routingInfo.startPoint = {
              x: section.startPoint.x,
              y: section.startPoint.y,
            };
          }

          // Add bend points
          if (section.bendPoints) {
            for (const bp of section.bendPoints) {
              routingInfo.bendPoints.push({
                x: bp.x,
                y: bp.y,
              });
            }
          }

          // Add end point
          if (section.endPoint) {
            routingInfo.endPoint = {
              x: section.endPoint.x,
              y: section.endPoint.y,
            };
          }
        }
      }

      edgeRouting.set(elkEdge.id, routingInfo);
    }
  }

  return {
    nodes: layoutedNodes,
    edgeRouting,
  };
}
